import { ActiveWorkingRoleSelector } from "./ActiveWorkingRoleSelector";
import {
  ShieldCheck,
} from "lucide-react";
import { AuthScreen } from "../features/auth/AuthScreen";
import { Toast } from "../shared/components/Toast";
import { SystemState } from "../shared/components/SystemState";
import { AppHeader } from "./AppHeader";
import { AppSidebar } from "./AppSidebar";
import { ModuleView } from "./ModuleView";
import {
  type PreviewRole,
} from "./navigation";
import { useApplicationShell } from "./useApplicationShell";

export default function App() {
  const model = useApplicationShell();
  const {
    user,
    rolePreview,
    sessionReady,
    setSessionReady,
    sidebarOpen,
    setSidebarOpen,
    sidebarPinned,
    activeNav,
    offerFocusRequestNo,
    purchaseOrderFocusRequestNo,
    approvalFocusRequestNo,
    requestFocusRequestNo,
    supplierFocusQuery,
    globalNavigationVersion,
    dataRefreshVersion,
    toast,
    requestComposerOpen,
    setRequestComposerOpen,
    authRequired,
    sessionUnavailable,
    setSessionUnavailable,
    setSessionLoadVersion,
    navigateTo,
    openRequestWorkspace,
    openPurchaseOrder,
    returnSpecialistToHome,
    previousNav,
    goBack,
    handleNestedBackChange,
    activateRolePreview,
    exitRolePreview,
    notify,
    closeSidebar,
    logout,
  } = model;
  const localAuthPreview = import.meta.env?.DEV
    && ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)
    && new URLSearchParams(window.location.search).get("demoAuth") === "1";
  if (localAuthPreview) return <AuthScreen />;
  if (sessionUnavailable) {
    return (
      <AuthScreen
        mode="service-unavailable"
        onRetry={() => {
          setSessionUnavailable(false);
          setSessionReady(false);
          setSessionLoadVersion((current) => current + 1);
        }}
      />
    );
  }
  if (authRequired) {
    return <AuthScreen />;
  }
  if (!sessionReady) {
    return (
      <SystemState
        mode="page"
        tone="loading"
        title="Güvenli çalışma alanı hazırlanıyor"
        description="Kullanıcı rolünüz ve erişebileceğiniz veri kapsamı doğrulanıyor."
        assistance="Bu işlem normalde birkaç saniye sürer."
      />
    );
  }
  return (
    <main
      className={`app-shell ${sidebarPinned ? "sidebar-pinned" : ""} ${sidebarOpen || sidebarPinned ? "sidebar-visible" : ""
        } ${rolePreview ? "role-preview-active" : ""} ${user.role === "URETIM_OPERATORU" || user.role === "VARDIYA_AMIRI"
          ? "operator-kiosk-shell"
          : ""
        } ${user.role === "URETIM_YONETICISI" && activeNav === "Üretim"
          ? "production-manager-app-shell"
          : ""
        }`}
    >
      <button
        className="sidebar-edge-trigger"
        aria-label="Yan menüyü aç"
        aria-expanded={sidebarOpen || sidebarPinned}
        onMouseEnter={() => setSidebarOpen(true)}
        onFocus={() => setSidebarOpen(true)}
        onClick={() => setSidebarOpen(true)}
      />
      <AppSidebar model={model} />

      {sidebarOpen && !sidebarPinned ? (
        <button
          className="sidebar-backdrop"
          aria-label="Menüyü kapat"
          onClick={closeSidebar}
        />
      ) : null}

      <section className="workspace">
        <AppHeader model={model} />
        {(user.role === "VARDIYA_AMIRI" || (user.role === "URETIM_YONETICISI" && activeNav === "Üretim")) ? (
          <ActiveWorkingRoleSelector user={user} rolePreview={Boolean(rolePreview)}
            switching={model.roleSwitching} onSwitch={model.switchActiveRole}
            placement="production" />
        ) : null}

        {rolePreview ? (
          <section className="role-preview-banner" role="status">
            <div>
              <ShieldCheck size={18} />
              <p>
                <strong>Rol Önizleme Modu · {user.roleLabel}</strong>
                <span>
                  İzole demo verisi kullanılıyor. İşlemler gerçek kayıtlara,
                  dosyalara veya bildirimlere aktarılmaz.
                </span>
              </p>
            </div>
            <div>
              <select
                aria-label="Demo rolünü değiştir"
                value={rolePreview}
                onChange={(event) =>
                  void activateRolePreview(event.target.value as PreviewRole)
                }
              >
                <option value="PLANLAMA">Planlama</option>
                <option value="SATIN_ALMA">Satın Alma</option>
                <option value="SATIN_ALMA_MUDURU">Satın Alma Müdürü</option>
                <option value="GENEL_MUDUR">Genel Müdür</option>
                <option value="KALITE_UZMANI">Kalite Uzmanı</option>
                <option value="KALITE_MUDURU">Kalite Müdürü</option>
                <option value="URETIM_OPERATORU">Üretim Operatörü</option>
                <option value="VARDIYA_AMIRI">Vardiya Amiri</option>
                <option value="URETIM_YONETICISI">Üretim Yöneticisi</option>
                <option value="TEKNIK_MUDURU">Teknik Departman Müdürü</option>
                <option value="DEPO_GOREVLISI">Depo Görevlisi</option>
                <option value="CEP_DEPO_SORUMLUSU">Cep Depo Sorumlusu</option>
              </select>
              <button onClick={() => void exitRolePreview()}>
                Önizlemeyi kapat
              </button>
            </div>
          </section>
        ) : null}

        <div className="page">
          <ModuleView
            activeNav={activeNav}
            notify={notify}
            requestComposerOpen={requestComposerOpen}
            setRequestComposerOpen={setRequestComposerOpen}
            user={user}
            offerFocusRequestNo={offerFocusRequestNo}
            purchaseOrderFocusRequestNo={purchaseOrderFocusRequestNo}
            approvalFocusRequestNo={approvalFocusRequestNo}
            requestFocusRequestNo={requestFocusRequestNo}
            supplierFocusQuery={supplierFocusQuery}
            globalNavigationVersion={globalNavigationVersion}
            dataRefreshVersion={dataRefreshVersion}
            onNavigate={navigateTo}
            onNestedBackChange={handleNestedBackChange}
            onOpenRequestWorkspace={openRequestWorkspace}
            onOpenPurchaseOrder={openPurchaseOrder}
            onReturnToOffers={(requestNo) =>
              openRequestWorkspace(requestNo, "offers")
            }
            onSpecialistDecisionSubmitted={returnSpecialistToHome}
            onLogout={logout}
            onProductionBack={
              rolePreview
                ? () => void exitRolePreview()
                : previousNav
                  ? goBack
                  : undefined
            }
          />
        </div>
      </section>

      <Toast message={toast.message} tone={toast.tone} onDismiss={model.dismissToast} />
    </main>
  );
}
